import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import api, { LOGOUT_ON_401 } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Inactivity timeout in milliseconds (default 30 minutes, configurable via env)
const INACTIVITY_TIMEOUT = Number(import.meta.env.VITE_INACTIVITY_TIMEOUT || 1800000); // 30 minutes default
// Token check interval (check every 5 minutes)
const TOKEN_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
// Allow 60s clock skew so token isn't rejected on reload
const TOKEN_EXP_BUFFER_MS = 60000;

/** Restore user from token synchronously so we never flash "not logged in" or white screen on reload. */
function restoreUserFromToken() {
  try {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('merchant_token') : null;
    if (!token) return null;
    const decoded = jwtDecode(token);
    const expMs = decoded.exp * 1000;
    if (expMs <= Date.now() - TOKEN_EXP_BUFFER_MS) {
      localStorage.removeItem('merchant_token');
      return null;
    }
    // Restore is_admin from every possible JWT claim so admin stays admin after reload
    const role = decoded.user?.role?.name ?? decoded.role;
    const roleStr = typeof role === 'string' ? role.toUpperCase() : '';
    const isAdmin =
      decoded.is_admin === true ||
      decoded.user?.is_admin === true ||
      decoded.admin === true ||
      roleStr === 'ADMIN';
    return {
      id: decoded.user?.id ?? decoded.id ?? decoded.sub,
      name: decoded.user?.name ?? decoded.name ?? decoded.merchant_name ?? decoded.Name ?? '',
      email: decoded.user?.email ?? decoded.email ?? decoded.Email ?? '',
      role: role ?? (isAdmin ? 'Admin' : 'Merchant'),
      merchantID: decoded.merchantID ?? decoded.merchant_id ?? decoded.user?.id ?? decoded.id,
      baseCurrency: decoded.baseCurrency ?? 'USD',
      permissions: decoded.user?.role?.permissions?.split(',') ?? ['read', 'write'],
      is_admin: !!isAdmin,
    };
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(restoreUserFromToken);
  const [loading, setLoading] = useState(false);
  const inactivityTimerRef = useRef(null);
  const tokenCheckIntervalRef = useRef(null);
  // Guard against multiple simultaneous logout calls
  const loggingOutRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (tokenCheckIntervalRef.current) {
      clearInterval(tokenCheckIntervalRef.current);
      tokenCheckIntervalRef.current = null;
    }
  }, []);

  // Soft logout: clears token + sets user to null.
  // ProtectedRoute will see user===null and <Navigate to="/login"> with no hard reload.
  const softLogout = useCallback(() => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    localStorage.removeItem('merchant_token');
    clearTimers();
    setUser(null);
    // Reset guard after a tick so future logouts can still fire
    setTimeout(() => { loggingOutRef.current = false; }, 100);
  }, [clearTimers]);

  const handleUnauthorized = useCallback(() => {
    // Already logged out — nothing to do
    if (!localStorage.getItem('merchant_token')) return;
    softLogout();
  }, [softLogout]);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    if (user) {
      inactivityTimerRef.current = setTimeout(() => {
        console.log('User inactive for', INACTIVITY_TIMEOUT / 1000 / 60, 'minutes. Logging out...');
        softLogout();
      }, INACTIVITY_TIMEOUT);
    }
  }, [user, softLogout]);

  const checkTokenExpiration = useCallback(() => {
    if (!user) return;

    const token = localStorage.getItem('merchant_token');
    if (!token) {
      softLogout();
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const expirationTime = decoded.exp * 1000;
      const currentTime = Date.now();
      const timeUntilExpiry = expirationTime - currentTime;

      if (timeUntilExpiry < 60000) {
        console.log('Token expired or expiring soon. Logging out...');
        softLogout();
      }
    } catch (error) {
      console.error('Token check error:', error);
      softLogout();
    }
  }, [user, softLogout]);

  // Set up activity tracking and token checking
  useEffect(() => {
    if (!user) {
      clearTimers();
      return;
    }

    resetInactivityTimer();

    tokenCheckIntervalRef.current = setInterval(() => {
      checkTokenExpiration();
    }, TOKEN_CHECK_INTERVAL);

    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ];

    const lastActivityTimeRef = { current: 0 };
    const throttledHandleActivity = () => {
      const now = Date.now();
      if (now - lastActivityTimeRef.current > 1000) {
        lastActivityTimeRef.current = now;
        resetInactivityTimer();
      }
    };

    activityEvents.forEach((event) => {
      document.addEventListener(event, throttledHandleActivity, true);
    });

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, throttledHandleActivity, true);
      });
      clearTimers();
    };
  }, [user, resetInactivityTimer, checkTokenExpiration, clearTimers]);

  // Hydrate user profile from API if name is missing (e.g. after reload when JWT lacks name)
  useEffect(() => {
    if (!user) return;
    if (user.name && user.name !== '') return; // already have a name
    const token = localStorage.getItem('merchant_token');
    if (!token) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/v1/merchants/profile');
        const d = res.data?.data ?? res.data?.merchant ?? res.data;
        if (!cancelled && d) {
          setUser((prev) => ({
            ...prev,
            name: d.name || d.Name || d.merchant_name || prev.name,
            email: d.email || d.Email || prev.email,
            is_admin: d.is_admin ?? prev.is_admin,
            role: d.is_admin ? 'Admin' : (prev.role || 'Merchant'),
          }));
        }
      } catch {
        // profile endpoint may not exist — keep JWT data
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Register 401 listener
  useEffect(() => {
    if (LOGOUT_ON_401) {
      window.addEventListener('auth:unauthorized', handleUnauthorized);
    }
    return () => {
      if (LOGOUT_ON_401) {
        window.removeEventListener('auth:unauthorized', handleUnauthorized);
      }
    };
  }, [handleUnauthorized]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/api/v1/merchants/login', {
        email: email,
        password: password
      });

      if (response.data && response.data.token) {
        const token = response.data.token;
        const userData = response.data.user || response.data;

        localStorage.setItem('merchant_token', token);

        const userObject = {
          id: userData.id || userData.ID,
          name: userData.name || userData.Name,
          email: userData.email || userData.Email,
          role: userData.is_admin ? 'Admin' : 'Merchant',
          merchantID: userData.id || userData.ID,
          baseCurrency: 'USD',
          permissions: ['read', 'write'],
          location: userData.location,
          phone: userData.phone,
          is_admin: userData.is_admin || false
        };

        loggingOutRef.current = false;
        setUser(userObject);
        return { success: true };
      } else {
        return { success: false, error: 'Invalid response from server - no token received' };
      }
    } catch (error) {
      console.error('AuthContext: Login error:', error);

      if (error.response) {
        return {
          success: false,
          error: error.response.data?.message || `Server error: ${error.response.status}`
        };
      } else if (error.request) {
        return {
          success: false,
          error: 'No response from server. Please check your internet connection.'
        };
      } else {
        return {
          success: false,
          error: 'Login failed. Please check your credentials.'
        };
      }
    }
  };

  const logout = useCallback(() => {
    softLogout();
  }, [softLogout]);

  const isAdmin = user?.is_admin === true;

  const value = {
    user,
    login,
    logout,
    loading,
    api,
    isAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
